import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Phone, ShieldCheck, BellRing, Send } from "lucide-react";

/**
 * SmsSettings — phone verification + SMS alerts opt-in.
 *
 * Lives inside VesselSettings but operates on the *user* (not the vessel)
 * because SMS preferences are account-level. Surfaces 4 states:
 *   1. Backend has no Twilio creds → shows "SMS is being configured" notice
 *   2. No phone on file → "Add your phone" form
 *   3. Phone on file but not verified → 6-digit code input + "Send code"
 *   4. Phone verified → opt-in toggle + "Send test SMS" + "Scan now" buttons
 */
export default function SmsSettings({ canWrite = true }) {
    const [status, setStatus] = useState(null);
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [info, setInfo] = useState("");

    const reload = async () => {
        try {
            const { data } = await api.get("/sms/status");
            setStatus(data);
            setPhone(data.phone || "");
        } catch (e) {
            // Endpoint failure (network down, 5xx) — degrade gracefully but log
            // so it's diagnosable in the browser console.
            console.warn("[sms] /sms/status failed, treating as unavailable", e?.response?.status);
            setStatus({ sms_available: false });
        }
    };

    useEffect(() => { reload(); }, []);

    const submit = async (fn, successMsg) => {
        setBusy(true); setErr(""); setInfo("");
        try {
            await fn();
            setInfo(successMsg);
            await reload();
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    if (!status) return null;

    const headingRow = (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <h3 className="bm-display" style={{ margin: 0, fontSize: 18, color: "var(--bm-gold)", display: "flex", alignItems: "center", gap: 8 }}>
                <BellRing size={16} /> SMS alerts
            </h3>
            {status.sms_available && status.phone_verified && (
                <span data-testid="sms-verified-badge" className="bm-tag bm-tag-good" style={{ fontSize: 11 }}>verified</span>
            )}
        </div>
    );

    // State 1 — backend not configured
    if (!status.sms_available) {
        return (
            <section className="bm-card" data-testid="sms-settings" style={{ padding: 24, marginBottom: 22, opacity: 0.85 }}>
                {headingRow}
                <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: 0, lineHeight: 1.6 }}>
                    SMS alerts are being configured. Check back soon — when this is ready you'll be able to opt in here and receive a text message when maintenance is overdue or upcoming.
                </p>
            </section>
        );
    }

    return (
        <section className="bm-card" data-testid="sms-settings" style={{ padding: 24, marginBottom: 22 }}>
            {headingRow}
            <p style={{ color: "var(--bm-text-muted)", fontSize: 13, margin: "0 0 16px", lineHeight: 1.6 }}>
                Get a text when maintenance is overdue or coming up within the next 7 days / 10 engine hours. One consolidated message per vessel, max once per day.
            </p>

            {/* State 2 — no phone, or changing phone */}
            {!status.phone_verified && (
                <div data-testid="sms-add-phone" style={{ display: "grid", gap: 12 }}>
                    <div>
                        <label className="bm-label" style={{ fontSize: 11 }}>Mobile number (E.164)</label>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <input
                                data-testid="sms-phone-input"
                                className="bm-input"
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+14155551212"
                                style={{ flex: 1, minWidth: 220 }}
                                disabled={!canWrite || busy}
                            />
                            <button
                                data-testid="sms-send-code-btn"
                                className="bm-btn bm-btn-gold"
                                disabled={!canWrite || busy || !phone}
                                onClick={() => submit(() => api.post("/sms/phone", { phone }), "Code sent — check your phone.")}
                                style={{ padding: "8px 14px" }}
                            >
                                <Phone size={14} /> {status.phone ? "Resend code" : "Send code"}
                            </button>
                        </div>
                    </div>
                    {status.phone && (
                        <div style={{ display: "grid", gap: 6 }}>
                            <label className="bm-label" style={{ fontSize: 11 }}>6-digit code we just texted to {status.phone}</label>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <input
                                    data-testid="sms-code-input"
                                    className="bm-input"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                                    placeholder="123456"
                                    style={{ flex: 1, minWidth: 160, fontVariantNumeric: "tabular-nums", letterSpacing: "0.3em" }}
                                    disabled={!canWrite || busy}
                                />
                                <button
                                    data-testid="sms-verify-btn"
                                    className="bm-btn bm-btn-gold"
                                    disabled={!canWrite || busy || code.length < 4}
                                    onClick={() => submit(() => api.post("/sms/verify", { code }), "Phone verified. SMS alerts are now on.")}
                                    style={{ padding: "8px 14px" }}
                                >
                                    <ShieldCheck size={14} /> Verify
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* State 4 — verified */}
            {status.phone_verified && (
                <div data-testid="sms-verified-panel" style={{ display: "grid", gap: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <div>
                            <div style={{ fontSize: 13, color: "var(--bm-text)", fontWeight: 500 }}>
                                Texting to <span style={{ color: "var(--bm-gold)" }}>{status.phone}</span>
                            </div>
                            <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 2 }}>
                                Standard message rates apply. Reply STOP to opt out at any time.
                            </div>
                        </div>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input
                                data-testid="sms-toggle-input"
                                type="checkbox"
                                checked={!!status.sms_alerts_enabled}
                                onChange={(e) => submit(
                                    () => api.post("/sms/toggle", { enabled: e.target.checked }),
                                    e.target.checked ? "SMS alerts on." : "SMS alerts off."
                                )}
                                disabled={busy}
                            />
                            <span style={{ fontSize: 13, color: "var(--bm-text)" }}>
                                {status.sms_alerts_enabled ? "Alerts ON" : "Alerts OFF"}
                            </span>
                        </label>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                            data-testid="sms-test-btn"
                            className="bm-btn bm-btn-ghost"
                            disabled={busy}
                            onClick={() => submit(() => api.post("/sms/test"), "Test SMS sent — check your phone.")}
                            style={{ padding: "8px 12px", fontSize: 13 }}
                        >
                            <Send size={13} /> Send test SMS
                        </button>
                        <button
                            data-testid="sms-scan-btn"
                            className="bm-btn bm-btn-ghost"
                            disabled={busy || !status.sms_alerts_enabled}
                            onClick={() => submit(
                                () => api.post("/sms/scan-maintenance"),
                                "Scan complete — alerts sent for any items that needed one."
                            )}
                            style={{ padding: "8px 12px", fontSize: 13 }}
                        >
                            <BellRing size={13} /> Scan maintenance now
                        </button>
                        <button
                            data-testid="sms-change-phone-btn"
                            className="bm-btn bm-btn-ghost"
                            disabled={busy}
                            onClick={async () => {
                                if (!window.confirm("Change phone number? You'll need to verify the new one with a code.")) return;
                                setBusy(true); setErr(""); setInfo("");
                                try {
                                    // Disable alerts so the user isn't texted during the transition
                                    await api.post("/sms/toggle", { enabled: false });
                                } catch (e) {
                                    // Non-fatal — proceed with the local reset so the user can still
                                    // enter a new number. We just log for debugging.
                                    console.warn("[sms] could not pre-disable alerts during phone change", e?.response?.status, e?.response?.data);
                                }
                                await reload();
                                setBusy(false);
                                setInfo("Enter a new phone number above and request a fresh code.");
                            }}
                            style={{ padding: "8px 12px", fontSize: 13, color: "var(--bm-text-muted)" }}
                        >
                            Change phone
                        </button>
                    </div>
                </div>
            )}

            {info && (
                <div data-testid="sms-info" style={{ marginTop: 12, padding: "8px 12px", borderRadius: "var(--bm-r-sm)", background: "rgba(34, 197, 94, 0.10)", color: "#bbf7d0", fontSize: 12 }}>
                    {info}
                </div>
            )}
            {err && (
                <div data-testid="sms-error" style={{ marginTop: 12, padding: "8px 12px", borderRadius: "var(--bm-r-sm)", background: "rgba(248, 113, 113, 0.10)", color: "#fca5a5", fontSize: 12 }}>
                    {err}
                </div>
            )}
        </section>
    );
}
